import { Hint } from '@/components/ui';

import React, {
  InputHTMLAttributes,
  ReactNode,
  forwardRef,
  useId,
} from 'react';

import { TextField as RadixTextField } from '@radix-ui/themes';
import clsx from 'clsx';

import { Label } from './Label';

// Mapping for design system sizes
const sizeMapping = {
  small: '2',
  medium: '2',
  large: '3',
  default: '3',
} as const;

// Mapping for design system variants
const variantMapping = {
  filled: 'soft',
  outlined: 'surface',
  ghost: 'surface',
  classic: 'classic',
} as const;

// Mapping for design system colors to Radix UI colors
const colorMapping = {
  default: 'gray',
  primary: 'blue',
  secondary: 'gray',
  error: 'red',
} as const;

// Only allowed types for Radix TextField
export type TextFieldType =
  | 'number'
  | 'hidden'
  | 'search'
  | 'time'
  | 'text'
  | 'email'
  | 'tel'
  | 'url'
  | 'date'
  | 'datetime-local'
  | 'month'
  | 'password'
  | 'week';

export interface TextFieldProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'size' | 'color' | 'type' | 'value' | 'defaultValue'
  > {
  label?: string;
  placeholder?: string;
  required?: boolean;
  startSlot?: ReactNode;
  endSlot?: ReactNode;
  size?: keyof typeof sizeMapping;
  color?: keyof typeof colorMapping;
  variant?: keyof typeof variantMapping;
  error?: boolean;
  helpText?: string;
  errorMessage?: string;
  className?: string;
  type?: TextFieldType;
  value?: string | number;
  defaultValue?: string | number;
}

/**
 * TextField component is a design system adapter for Radix TextField.
 * It supports slots, design system props mapping, and is ready for extension.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      placeholder,
      required,
      startSlot,
      endSlot,
      size = 'large',
      color = 'primary',
      variant = 'outlined',

      error = false,
      helpText,
      errorMessage,
      className,
      type = 'text',
      value,
      defaultValue,
      ...props
    },
    ref
  ) => {
    const inputId = useId();
    const mappedSize = sizeMapping[size] || sizeMapping.default;
    const mappedVariant = variantMapping[variant] || variantMapping.outlined;
    const mappedColor = error
      ? colorMapping.error
      : colorMapping[color as keyof typeof colorMapping] ||
        colorMapping.default;

    return (
      <div className={clsx('textfield-wrapper', className)}>
        {label && (
          <Label htmlFor={inputId} className={clsx({ required })}>
            {label} {required && <span className="textfield-required">*</span>}
          </Label>
        )}
        <RadixTextField.Root
          id={inputId}
          placeholder={placeholder}
          color={mappedColor as typeof mappedColor}
          size={mappedSize as typeof mappedSize}
          variant={mappedVariant as typeof mappedVariant}
          radius="large"
          ref={ref}
          className={clsx(
            `size-${size}`,
            `variant-${variant}`,
            { error },
            'textfield-root'
          )}
          data-error={error}
          type={type}
          value={value}
          defaultValue={defaultValue}
          {...props}
        >
          {startSlot && <RadixTextField.Slot>{startSlot}</RadixTextField.Slot>}
          {endSlot && <RadixTextField.Slot>{endSlot}</RadixTextField.Slot>}
        </RadixTextField.Root>
        {(errorMessage || helpText) && (
          <Hint hasError={error}>{error ? errorMessage : helpText}</Hint>
        )}
      </div>
    );
  }
);

TextField.displayName = 'TextField';
